/**
 * Compare to previous period:
 * - Series charts that support it:
 *   - Column chart
 *   - Line chart
 *
 * - Support for single and multi metric charts.
 *
 * - We render the first series as usual, with the color palette.
 * - The previous period series is rendered with the same color palette, but with an opacity of 0.7
 *
 * - Chronological alignment: When previous and current periods have different numbers of data points,
 *   we align them chronologically (Week 1 to Week 1, Week 2 to Week 2, etc.)
 */
import {
  ChartSeries,
  ChartSeriesDataPoint,
} from '@/features/report-snapshots/components/charts/utils/chart-data';
import type { DateRange, Timezone } from '@/features/report-snapshots/api/schema';
import type { I18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { SERIES_COLOR_PALETTE } from '@/features/report-snapshots/constants';
import {
  getDateRangeDayCount,
  formatDateRangeWithoutYear,
} from '@/features/report-snapshots/formatters/date-range-formatter';

function applyOpacity(color: string | undefined) {
  if (!color) return undefined;

  if (color.startsWith('var(--') && color.endsWith('-50)')) {
    return color.replace('-50)', '-70)');
  }

  return color;
}

/**
 * Align previous series data to match current series length using chronological alignment.
 * - If previous has MORE data points: trim from the end (keep first N)
 * - If previous has FEWER data points: pad at the end with nulls
 */
function alignSeriesData(
  previousData: ChartSeriesDataPoint[],
  currentData: ChartSeriesDataPoint[],
): ChartSeriesDataPoint[] {
  const currentLength = currentData.length;
  const previousLength = previousData.length;

  if (previousLength === currentLength) {
    return previousData;
  }

  if (previousLength > currentLength) {
    // Chronological alignment: keep the FIRST N data points (trim from end)
    return previousData.slice(0, currentLength);
  }

  // Chronological alignment: pad at the END with nulls
  const paddingCount = currentLength - previousLength;
  const padding = Array(paddingCount).fill({ y: null, name: '' }) as ChartSeriesDataPoint[];
  return [...previousData, ...padding];
}

export function comparisonSeries(
  previousSeries: ChartSeries[],
  currentSeries: ChartSeries[],
  dateRange: DateRange,
  timezone: Timezone,
  i18n: I18n,
  isMultimetric: boolean,
): ChartSeries[] {
  // Assign explicit colors to current series from palette
  const currentSeriesWithColors = currentSeries.map((series, index) => ({
    ...series,
    color: series.color ?? SERIES_COLOR_PALETTE[index % SERIES_COLOR_PALETTE.length],
    name: formatCurrentDateRangeLabel(series.name, dateRange, timezone, i18n, isMultimetric),
  }));

  // Create previous series using the same colors but with opacity
  // AND align data arrays chronologically
  const previousSeriesWithOpacity = previousSeries.map((series, index) => {
    const baseColor =
      currentSeriesWithColors[index]?.color ??
      SERIES_COLOR_PALETTE[index % SERIES_COLOR_PALETTE.length];

    // Align previous series data to match current series data length
    const alignedData = alignSeriesData(series.data, currentSeriesWithColors[index].data);

    return {
      ...series,
      name: formatPreviousDateRangeLabel(series.name, dateRange, timezone, i18n, isMultimetric),
      color: applyOpacity(baseColor),
      data: alignedData.map((dataPoint: ChartSeriesDataPoint, dataIndex) => {
        const baseDataColor =
          currentSeriesWithColors[index]?.data[dataIndex]?.color ?? dataPoint.color;
        return {
          ...dataPoint,
          color: applyOpacity(baseDataColor),
        };
      }),
    };
  });

  // Return current series first (normal rendering) followed by previous series (with opacity)
  return [...currentSeriesWithColors, ...previousSeriesWithOpacity];
}

/**
 * Formats a label for the previous period in comparison charts.
 * Returns a localized string like "Previous 7 days" or "Previous 1 day".
 */
function formatPreviousDateRangeLabel(
  seriesName: string,
  dateRange: DateRange,
  timezone: Timezone,
  i18n: I18n,
  isMultimetric: boolean,
): string {
  const numberOfDays = getDateRangeDayCount(dateRange, timezone);

  if (!numberOfDays) return seriesName;
  let daysLabel;

  if (numberOfDays === 1) {
    daysLabel = i18n._(msg`Previous ${numberOfDays} day`);
  } else {
    daysLabel = i18n._(msg`Previous ${numberOfDays} days`);
  }

  if (isMultimetric) {
    return `${seriesName} - ${daysLabel}`;
  }

  return daysLabel;
}

function formatCurrentDateRangeLabel(
  seriesName: string,
  dateRange: DateRange,
  timezone: Timezone,
  i18n: I18n,
  isMultimetric: boolean,
) {
  const label = formatDateRangeWithoutYear(dateRange, timezone, i18n);

  if (!label) return seriesName;

  if (isMultimetric) {
    return `${seriesName} - ${label}`;
  }

  return label;
}
